import { Injectable } from '@angular/core';
import { CommsService } from '@services/comms/comms.service';
import { find, includes } from 'lodash';

export type BehaviorTagUsersMode = 'off' | 'optional' | 'required';

export const BEHAVIOR_TAG_USERS_LABELS: Record<BehaviorTagUsersMode, string> = {
  off: 'SHARED.Off',
  optional: 'SHARED.Optional',
  required: 'SHARED.Required'
};

interface ISubscriberSetting {
  settingID: number;
  lid: number;
  zid: number;
  value: string;
}

/**
 * The subscriber setting `behaviorTagUsers` (CVX-6668): whether coaching
 * opportunities offer, or require, tagging users. The row at location 0 is the
 * subscriber default (absent is off); a location's own row overrides it, so an
 * absent location row inherits and an explicit 'off' row turns it off there. Any
 * value other than off/optional/required reads as off. Writing it is sadmin-only
 * on the server.
 */
@Injectable({
  providedIn: 'root'
})
export class BehaviorTagUsersService {
  public static readonly settingName = 'behaviorTagUsers';
  private settings: ISubscriberSetting[] = [];

  constructor(private comms: CommsService) {
  }

  /** Resolves false (never rejects) when the settings could not be read; mode() then reads off. */
  public async refresh(): Promise<boolean> {
    const res = await this.comms.sendMessage({
      cmd: 'getSubscriberSettings',
      name: BehaviorTagUsersService.settingName,
      sendTime: Date.now()
    }, false, false).catch(() => null);
    const ok = res?.reqStatus === 'OK';
    this.settings = ok ? (res.result?.settings || []) : [];
    return ok;
  }

  /** The mode in effect at a location: its own row, else the subscriber default. */
  public mode(locationID: number): BehaviorTagUsersMode {
    return this.override(locationID) ?? this.subscriberMode();
  }

  public subscriberMode(): BehaviorTagUsersMode {
    return this.override(0) ?? 'off';
  }

  /** The location's own mode, or null when it inherits the subscriber default. */
  public override(locationID: number): BehaviorTagUsersMode | null {
    const row = this.find(locationID);
    if (!row) {
      return null;
    }
    return includes(['optional', 'required'], row.value) ? row.value as BehaviorTagUsersMode : 'off';
  }

  /**
   * Saves the mode for a location, or the subscriber default at location 0; null
   * removes a location's own row so it inherits again. Resolves true when the
   * server accepted it, false otherwise (never rejects).
   */
  public async setMode(locationID: number, mode: BehaviorTagUsersMode | null): Promise<boolean> {
    const existing = this.find(locationID);
    if (!existing && (mode === null || (mode === 'off' && !locationID))) {
      return true;
    }
    // addSubscriberSetting updates the row with the same name/location/zone
    const res = await this.comms.sendMessage(mode === null
      ? { cmd: 'deleteSubscriberSetting', settingID: existing.settingID, sendTime: Date.now() }
      : { cmd: 'addSubscriberSetting', name: BehaviorTagUsersService.settingName, locID: locationID, zoneID: 0, value: mode, sendTime: Date.now() },
      false, false).catch(() => null);
    await this.refresh();
    return res?.reqStatus === 'OK';
  }

  private find(locationID: number): ISubscriberSetting | undefined {
    return find(this.settings, (s) => Number(s.lid) === Number(locationID) && !Number(s.zid));
  }
}
